/**
 * asnLoadsComparisonAdapter.ts
 *
 * Pure data-transformation layer for the ASN Gap Overview tab's "ASN Loads
 * vs Non-ASN Loads" chart (`ASNLoadsComparison`), backed by
 * `POST /asn/loads-vs-non-asn/chart`.
 *
 * This module intentionally contains NO React/rendering code so it can be
 * unit-tested in isolation. `AsnGapDashboard` fetches this endpoint with the
 * currently-selected Chart Configuration → Location Hierarchy grain sent as
 * `locationHierarchy` (REGION/MARKET/LOCATION) and stores the response's
 * flat `rows` array as-is; a Location Hierarchy change re-fetches
 * immediately with the new `locationHierarchy`, while clicking a bar
 * (in-chart drill-down) or navigating Back stays purely client-side —
 * every visible drill-down view within the current grain is derived from
 * that single cached array by the pure helpers below, never issuing another
 * request.
 *
 * Filtering is always done against `grainVals` (the row's actual parent
 * lineage), never against `label` text, so scoped drill-down cannot
 * accidentally match an unrelated row that happens to share a display
 * name.
 */

import type { OverviewHierarchyLevel } from '../components/Overview';
import {
  LoadsVsNonAsnChartRow,
  LoadsVsNonAsnChartRowGrainVals,
  LoadsVsNonAsnChartSortBy,
} from '../api/asnOverviewKpiApi';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Fully-derived, render-ready row for a single bar in the comparison chart. */
export interface AsnLoadsChartItem {
  /** Stable identity built from the row's own hierarchy level + lineage —
   * never derived from `label` alone, so two different locations that
   * happen to share a display name can never collide. */
  id: string;
  label: string;
  nonASNLoads: number;
  asnLoads: number;
  totalLoads: number;
  /** = `row.pctNonAsn`, clamped to [0, 100]. */
  nonASNPercentage: number;
  /** = `row.targetPct` — backend-evaluated target for this row. */
  targetPercentage: number;
  /** = `row.isAboveTarget` — backend-evaluated; never recomputed locally. */
  isAboveTarget: boolean;
  hierarchyLevel: 0 | 1 | 2;
  grainVals: LoadsVsNonAsnChartRowGrainVals;
  parentLabel?: string;
  parentGrainVals?: LoadsVsNonAsnChartRowGrainVals;
}

/** The chart's current drill-down position. `drillRegion`/`drillMarket` are
 * the only new pieces of state this feature needs — `hierarchyLevel` is
 * always the same Chart Configuration → Location Hierarchy selection
 * `AsnGapDashboard` already owns (`chartHierarchyLevel`), reused here
 * rather than duplicated. */
export interface AsnChartDrillState {
  hierarchyLevel: OverviewHierarchyLevel;
  drillRegion: string | null;
  drillMarket: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Guards against NaN/Infinity/negative — never let a bad number reach the UI. */
const safeNumber = (value: number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value;
};

const clampPct = (value: number | null | undefined): number => {
  if (value === null || value === undefined || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const HIERARCHY_LEVEL_TO_NUM: Record<OverviewHierarchyLevel, 0 | 1 | 2> = {
  region: 0,
  market: 1,
  location: 2,
};

/** Chart Configuration → Location Hierarchy display labels for this chart's
 * title/subtitle (`ASN Loads vs Non-ASN Loads by {label}`). */
export const LOADS_VS_NON_ASN_HIERARCHY_LABELS: Record<OverviewHierarchyLevel, string> = {
  region: 'Region / Unit',
  market: 'Market / POD',
  location: 'Location',
};

const buildRowId = (row: LoadsVsNonAsnChartRow): string => {
  const parts = [row.hierarchyLevel, row.grainVals?.region, row.grainVals?.market, row.grainVals?.location].filter(
    (part) => part !== undefined && part !== null && part !== '',
  );
  return parts.length > 0 ? parts.join('::') : row.label;
};

/**
 * Maps one raw API row to a fully-derived, defensively-normalized display
 * item. Counts are clamped to sane values; `isAboveTarget`/`targetPercentage`
 * are taken directly from the backend and never recomputed.
 */
export const mapLoadsVsNonAsnRowToItem = (row: LoadsVsNonAsnChartRow): AsnLoadsChartItem => ({
  id: buildRowId(row),
  label: row.label,
  nonASNLoads: safeNumber(row.nonAsnLoads),
  asnLoads: safeNumber(row.asnLoads),
  totalLoads: safeNumber(row.totalLoads),
  nonASNPercentage: clampPct(row.pctNonAsn),
  targetPercentage: clampPct(row.targetPct),
  isAboveTarget: !!row.isAboveTarget,
  hierarchyLevel: row.hierarchyLevel,
  grainVals: row.grainVals ?? {},
  parentLabel: row.parentLabel,
  parentGrainVals: row.parentGrainVals,
});

/**
 * Defensive guard: a row whose own `label` is literally "Unknown" (any
 * casing/whitespace) is never rendered as a chart bar. This chart must only
 * ever show real, named Region/Market/Location bars — never a placeholder
 * for data that couldn't be attributed to one.
 */
const isUnknownLabel = (label: string | null | undefined): boolean =>
  String(label ?? '').trim().toLowerCase() === 'unknown';

/**
 * Derives the rows visible for the current hierarchy level + drill path
 * from the complete flat API response — purely a local filter, never a
 * new request.
 *
 *   region:   rows.filter(row => row.hierarchyLevel === 0)
 *   market:   level-1 rows, scoped to `drillRegion` when set (else all)
 *   location: level-2 rows, scoped to `drillRegion`/`drillMarket` when set
 *
 * Filtering always compares `grainVals` fields — never `label`. Rows with
 * an "Unknown" label are always excluded (see `isUnknownLabel`).
 */
export const getVisibleLoadsVsNonAsnItems = (
  rows: LoadsVsNonAsnChartRow[] | undefined | null,
  hierarchyLevel: OverviewHierarchyLevel,
  drillRegion: string | null,
  drillMarket: string | null,
): AsnLoadsChartItem[] => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const levelNum = HIERARCHY_LEVEL_TO_NUM[hierarchyLevel];

  return rows
    .filter((row) => {
      if (row.hierarchyLevel !== levelNum) return false;
      if (isUnknownLabel(row.label)) return false;
      if (levelNum === 0) {
        return true;
      }
      if (levelNum === 1) {
        if (drillRegion) {
          return row.parentGrainVals?.region === drillRegion;
        }
        return true;
      }
      if (levelNum === 2) {
        if (drillRegion && row.parentGrainVals?.region !== drillRegion) return false;
        if (drillMarket && row.parentGrainVals?.market !== drillMarket) return false;
        return true;
      }
      return true;
    })
    .map(mapLoadsVsNonAsnRowToItem);
};

/**
 * Cross-references already-derived chart items (from
 * `getVisibleLoadsVsNonAsnItems`) against the authoritative Market/Location
 * list returned by the ASN location dropdown API
 * (`fetchAsnLocationDropdownFilters`), which reflects the real
 * Region -> Market -> Location relationship used by the shared filter
 * dropdowns.
 *
 * Only an item whose own grain value (its Market id at the 'market' level,
 * or its Location id at the 'location' level) is present in
 * `validOptionValues` is kept — this guarantees the chart drill-down only
 * ever shows a bar for a value the dropdown relationship actually
 * recognizes, instead of any stray/mismatched row that might otherwise slip
 * through the flat chart response.
 *
 * `validOptionValues === null` means "no cross-reference constraint applies"
 * (used at the 'region' level, where every row is a top-level bucket the
 * backend itself grouped by — nothing to cross-reference against) and
 * returns `items` unchanged. `validOptionValues === []` means the dropdown
 * relationship found nothing for the current drill path, so the result is
 * always empty (renders the chart's existing empty state).
 */
export const filterChartItemsByValidValues = (
  items: AsnLoadsChartItem[],
  validOptionValues: string[] | null,
): AsnLoadsChartItem[] => {
  if (validOptionValues === null) return items;
  if (validOptionValues.length === 0) return [];
  const validSet = new Set(validOptionValues);
  return items.filter((item) => {
    const ownValue = item.hierarchyLevel === 1 ? item.grainVals.market : item.grainVals.location;
    return !!ownValue && validSet.has(ownValue);
  });
};

// ─── Drill-down state transitions (pure — no React) ────────────────────────────

/**
 * A Region bar was clicked: drill into Market/POD scoped to that region.
 * A Market bar was clicked: drill into Location scoped to that region+market.
 * A Location bar was clicked: final level — no further drill (returns the
 * unchanged current state).
 */
export const computeBarClickDrillState = (
  current: AsnChartDrillState,
  item: Pick<AsnLoadsChartItem, 'hierarchyLevel' | 'grainVals'>,
): AsnChartDrillState => {
  if (item.hierarchyLevel === 0) {
    return {
      hierarchyLevel: 'market',
      drillRegion: item.grainVals.region ?? null,
      drillMarket: null,
    };
  }
  if (item.hierarchyLevel === 1) {
    return {
      hierarchyLevel: 'location',
      drillRegion: item.grainVals.region ?? current.drillRegion,
      drillMarket: item.grainVals.market ?? null,
    };
  }
  return current;
};

/**
 * A Chart Configuration → Location Hierarchy option was selected directly
 * (not via a bar click).
 *   Region / Unit -> clears the whole drill path.
 *   Market / POD / Location -> keeps whatever drill path already exists
 *     (per spec: "or all markets/locations when no ... path exists" — the
 *     existing path, if any, is preserved rather than cleared).
 */
export const computeDirectHierarchySelection = (
  level: OverviewHierarchyLevel,
  current: AsnChartDrillState,
): AsnChartDrillState => {
  if (level === 'region') {
    return { hierarchyLevel: 'region', drillRegion: null, drillMarket: null };
  }
  return { ...current, hierarchyLevel: level };
};

/**
 * Back navigation: Location -> Market -> Region, updating Chart
 * Configuration during each step. A no-op at the Region level (nothing to
 * go back to).
 */
export const computeBackDrillState = (current: AsnChartDrillState): AsnChartDrillState => {
  if (current.hierarchyLevel === 'location') {
    return { hierarchyLevel: 'market', drillRegion: current.drillRegion, drillMarket: null };
  }
  if (current.hierarchyLevel === 'market') {
    return { hierarchyLevel: 'region', drillRegion: null, drillMarket: null };
  }
  return current;
};

// ─── Sort control ─────────────────────────────────────────────────────────────

export interface LoadsVsNonAsnSortOption {
  key: LoadsVsNonAsnChartSortBy;
  label: string;
  /** ASC for the alphabetical option; DESC (existing direction behavior)
   * for every numeric option. */
  direction: 'ASC' | 'DESC';
}

export const LOADS_VS_NON_ASN_SORT_OPTIONS: LoadsVsNonAsnSortOption[] = [
  { key: 'totalLoads', label: 'Most submissions', direction: 'DESC' },
  { key: 'nonAsnLoads', label: 'Most future plans submitted', direction: 'DESC' },
  { key: 'pctNonAsn', label: 'Largest gap', direction: 'DESC' },
  { key: 'label', label: 'Location A to Z', direction: 'ASC' },
];

export const getSortDirectionForOption = (sortBy: LoadsVsNonAsnChartSortBy): 'ASC' | 'DESC' =>
  LOADS_VS_NON_ASN_SORT_OPTIONS.find((opt) => opt.key === sortBy)?.direction ?? 'DESC';

// ─── Formatting (shared with BottomPerformingNonASN / NonASNLoadTrend) ────────

export const formatCount = (value: number): string =>
  Math.round(safeNumber(value)).toLocaleString('en-US');

export const formatPercent1 = (value: number): string => `${safeNumber(value).toFixed(1)}%`;

export const formatPercent2 = (value: number): string => `${safeNumber(value).toFixed(2)}%`;

export const formatPercentagePointDelta = (delta: number): string => {
  if (!Number.isFinite(delta) || delta === 0) return '0.0pp';
  const rounded = Math.round(delta * 10) / 10;
  const prefix = rounded > 0 ? '+' : '';
  return `${prefix}${rounded.toFixed(1)}pp`;
};

